import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { downloadJobFile } from "@/lib/jobFile";
/* Batch 11 · A9 (D11) — Audit Trail → "Sign-offs" sub-tab.
   Table: every form, vouching sheet, sampling sheet and worksheet with each active level's name + date and the flags
   ("Changed after sign-off · date", "Signed but empty"); filters by level, folder, missing only, flagged only; export CSV /
   Excel through the same background export as the other Audit Trail exports.
   Timeline: every sign / date change / remove / level removal, ordered by the sign-off date. Dates DD-MMM-YYYY. */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { fmtDate } from "@/lib/dt";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { asArray } from "@/lib/asArray";

export default function SignoffsTab({ t, engId }) {
  const [tab, setTab] = useState(null);
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("table");
  const [f, setF] = useState({ level: "", folder: "", missing: false, flagged: false });
  const nLv = (tab?.levels || []).length;   // Step 11d · add-on: item, folder, one column per sign-off level, flags
  const soCols = useResizableColumns({ storageKey: `signoffs-${nLv}`, defaults: [260, 160, ...Array(nLv).fill(150), 160], mins: [140, 100, ...Array(nLv).fill(100), 100] });

  useEffect(() => {
    if (!engId) return;
    api.get(`/engagements/${engId}/signoffs`).then((r) => setTab(r.data)).catch((e) => toast.error(toErrMsg(e, "")));
    api.get(`/engagements/${engId}/signoffs/timeline`).then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, [engId]);

  const folders = useMemo(() => Array.from(new Set(asArray(tab?.rows).map((r) => r.folder))).sort(), [tab]);
  const rows = useMemo(() => asArray(tab?.rows).filter((r) => {
    if (f.folder && r.folder !== f.folder) return false;
    if (f.missing && (r.not_required || !(f.level ? r.missing.includes(f.level) : r.missing.length))) return false;   // Batch 33 · L3
    if (f.flagged && !(r.changed_after_signoff || r.signed_but_empty)) return false;
    return true;
  }), [tab, f]);

  const doExport = async (fmt) => {
    try {
      const r = await api.post(`/engagements/${engId}/w13/export`, { format: fmt, view: "signoffs", filters: f }, { _silent: true });
      const jid = r.data?.job_id;
      for (let i = 0; i < 30; i++) {
        await new Promise((rr) => setTimeout(rr, 400));
        const j = await api.get(`/jobs/${jid}`, { _silent: true });
        if (j.data?.status === "done") {        // Batch 25 · B3 (M19): download the finished export
          await downloadJobFile(jid, j.data?.result?.filename || `signoffs.${fmt}`);
          toast.success(t("w13.exportedOk").replace("{n}", String(j.data?.result?.rows || 0))); break;
        }
        if (j.data?.status === "failed") { toast.error(t("itemSignoff.failed")); break; }
      }
    } catch (e) { toast.error(toErrMsg(e, "Export failed")); }
  };

  if (!tab) return <div className="text-sm text-muted-foreground" data-testid="signoffs-loading">{t("common.loading")}</div>;
  const levels = tab.levels || [];
  const roll = tab.rollup?.engagement || {};
  const chkErr = tab.not_required_check_error;   // Batch 34 · P51-S6: the "not required" check failed (was swallowed)
  return (
    <div className="space-y-3" data-testid="signoffs-tab">
      {chkErr && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
             role="alert" data-testid="signoffs-not-required-check-error">
          {t("itemSignoff.notRequiredCheckFailed")}
        </div>
      )}
      <div className="flex flex-wrap gap-3 text-xs" data-testid="signoffs-rollup">
        {levels.map((lv) => (
          <span key={lv} className="rounded border px-2 py-0.5" data-testid={`signoffs-rollup-${lv}`}>
            {t(`itemSignoff.level.${lv}`)} {roll[lv]?.signed ?? 0} / {roll[lv]?.total ?? 0}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <div className="flex gap-1">
          {["table", "timeline"].map((k) => (
            <button key={k} type="button" onClick={() => setView(k)} data-testid={`signoffs-view-${k}`}
                    className={`rounded border px-2 py-1 ${view === k ? "bg-slate-900 text-white" : ""}`}>{t(`itemSignoff.${k}`)}</button>
          ))}
        </div>
        {view === "table" && (<>
          <select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className="rounded border px-1 py-1" data-testid="signoffs-filter-level">
            <option value="">{t("itemSignoff.allLevels")}</option>
            {levels.map((lv) => <option key={lv} value={lv}>{t(`itemSignoff.level.${lv}`)}</option>)}
          </select>
          <select value={f.folder} onChange={(e) => setF({ ...f, folder: e.target.value })} className="rounded border px-1 py-1" data-testid="signoffs-filter-folder">
            <option value="">{t("itemSignoff.allFolders")}</option>
            {folders.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <label className="flex items-center gap-1"><input type="checkbox" checked={f.missing} onChange={(e) => setF({ ...f, missing: e.target.checked })} data-testid="signoffs-filter-missing" />{t("itemSignoff.onlyMissing")}</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={f.flagged} onChange={(e) => setF({ ...f, flagged: e.target.checked })} data-testid="signoffs-filter-flagged" />{t("itemSignoff.onlyFlagged")}</label>
          <Button size="sm" variant="outline" onClick={() => doExport("csv")} data-testid="signoffs-export-csv">{t("itemSignoff.exportCsv")}</Button>
          <Button size="sm" variant="outline" onClick={() => doExport("xlsx")} data-testid="signoffs-export-xlsx">{t("itemSignoff.exportXlsx")}</Button>
        </>)}
      </div>
      {view === "table" ? (
        <div className="overflow-x-auto rounded border">
          <ResizableTable cols={soCols} className="w-full text-[11px]" data-testid="signoffs-table">
            <thead className="bg-slate-50 dark:bg-slate-900"><tr>
              <ResizableTh index={0} className="px-2 py-1 text-start">{t("itemSignoff.item")}</ResizableTh>
              <ResizableTh index={1} className="px-2 py-1 text-start">{t("itemSignoff.folder")}</ResizableTh>
              {levels.map((lv, i) => <ResizableTh key={lv} index={i + 2} className="px-2 py-1 text-start">{t(`itemSignoff.level.${lv}`)}</ResizableTh>)}
              <ResizableTh index={levels.length + 2} className="px-2 py-1 text-start">{t("itemSignoff.flags")}</ResizableTh>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={levels.length + 3} className="px-2 py-3 text-center text-muted-foreground">{t("itemSignoff.noItems")}</td></tr>}
              {rows.map((r) => (
                <tr key={`${r.item_type}:${r.item_key}`} className="border-t" data-testid={`signoffs-row-${r.item_type}-${r.item_key}`}>
                  <td className="px-2 py-1"><Link to={r.link} className="text-sky-700 underline">{r.title}</Link>
                    {r.na && <div className="text-[10px] text-muted-foreground" data-testid={`signoffs-na-${r.item_key}`}>{t("itemSignoff.na")} — {r.na_reason || t("itemSignoff.naNotRecorded")}</div>}
                    {r.not_required && <div className="text-[10px] text-muted-foreground" data-testid={`signoffs-not-required-${r.item_key}`}>{t("itemSignoff.noBalanceNotRequired")}</div>}</td>
                  <td className="px-2 py-1">{r.folder}</td>
                  {levels.map((lv) => {
                    const v = r.levels[lv];
                    return <td key={lv} className="px-2 py-1" data-testid={`signoffs-cell-${r.item_key}-${lv}`}>
                      {v ? <span>{v.name}<br /><span className="text-muted-foreground">{fmtDate(v.date)}</span></span> : <span className={r.not_required ? "text-muted-foreground" : "text-red-600"}>—</span>}
                    </td>;
                  })}
                  <td className="px-2 py-1 text-amber-700">
                    {levels.filter((lv) => r.levels[lv]?.changed_after_signoff).map((lv) => (
                      <div key={lv}>{t(`itemSignoff.level.${lv}`)}: {t("itemSignoff.changedAfter")} · {fmtDate(r.levels[lv].changed_after_signoff)}</div>
                    ))}
                    {r.signed_but_empty && !r.not_required && <div>{t("itemSignoff.signedEmpty")}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResizableTable>
        </div>
      ) : (
        <ol className="space-y-1 text-xs" data-testid="signoffs-timeline">
          {events.length === 0 && <li className="text-muted-foreground">{t("itemSignoff.noEvents")}</li>}
          {events.map((e) => (
            <li key={e.id} className="rounded border px-2 py-1" data-testid={`signoffs-event-${e.action}`}>
              <b>{fmtDate(e.date)}</b> · {e.name} {t(`itemSignoff.ev.${e.action}`)} · {t(`itemSignoff.level.${e.level}`)} · {e.item_title}
              {e.action === "date_changed" && e.prev_date ? ` (${fmtDate(e.prev_date)} → ${fmtDate(e.date)})` : ""}
              {e.action === "level_removed" && e.signer_name ? ` (${e.signer_name})` : ""}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
